/**
 * PageLinkPicker — Toolbar popover for inserting a link to another page.
 *
 * Shows a searchable list of all accessible libraries and their pages.
 * On selection, inserts a hyperlink into the editor pointing to the selected page.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as pagesApi from '../../api/pages-api';
import type { Library, PageTreeNode } from '../../api/pages-api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PageLinkPickerProps {
  readonly onSelect: (libraryId: string, pageId: string, title: string) => void;
  readonly disabled?: boolean;
  readonly externalOpen?: boolean;
  readonly onExternalClose?: () => void;
}

interface FlatPage {
  readonly pageId: string;
  readonly title: string;
  readonly libraryId: string;
  readonly libraryName: string;
  readonly depth: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PageLinkPicker({ onSelect, disabled, externalOpen, onExternalClose }: PageLinkPickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);

  // Sync with external open control
  useEffect(() => {
    if (externalOpen) {
      setOpen(true);
    }
  }, [externalOpen]);

  // Notify parent when closing
  const handleClose = useCallback(() => {
    setOpen(false);
    onExternalClose?.();
  }, [onExternalClose]);
  const [query, setQuery] = useState('');
  const [libraries, setLibraries] = useState<Library[]>([]);
  const [pages, setPages] = useState<FlatPage[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, handleClose]);

  // Focus input on open
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Load libraries and pages when opened
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const libResponse = await pagesApi.listLibraries();
        const libs = libResponse.data ?? (libResponse as any).libraries ?? [];
        if (cancelled) return;
        setLibraries(libs);

        // Fetch page trees for all libraries in parallel
        const pageLists = await Promise.all(
          libs.map(async (lib: Library) => {
            try {
              const pagesResponse = await pagesApi.listPages(lib.libraryId);
              const tree = pagesResponse.data ?? (pagesResponse as any).pages ?? [];
              return { library: lib, tree };
            } catch {
              return { library: lib, tree: [] };
            }
          })
        );

        if (cancelled) return;

        // Flatten trees into a searchable list
        const flat: FlatPage[] = [];
        for (const { library, tree } of pageLists) {
          flattenTree(tree, library.libraryId, library.name, 0, flat);
        }
        setPages(flat);
      } catch {
        // Error loading — show empty state
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open]);

  // Filter pages by search query
  const filteredPages = query.trim()
    ? pages.filter(
        (p) =>
          p.title.toLowerCase().includes(query.toLowerCase()) ||
          p.libraryName.toLowerCase().includes(query.toLowerCase())
      )
    : pages;

  const handleSelect = useCallback(
    (page: FlatPage) => {
      onSelect(page.libraryId, page.pageId, page.title);
      handleClose();
      setQuery('');
    },
    [onSelect, handleClose]
  );

  return (
    <div ref={ref} className="relative">
      {/* Only show the trigger button if not externally controlled */}
      {externalOpen === undefined && (
      <button
        type="button"
        title="Link to page"
        aria-label="Link to page"
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={`px-1.5 py-1 text-sm font-medium rounded transition-colors
          ${open ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'}
          ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        📄
      </button>
      )}

      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-50 w-80 rounded-md border bg-popover shadow-lg"
          role="dialog"
          aria-label="Link to page picker"
        >
          {/* Search input */}
          <div className="p-2 border-b">
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search pages..."
              className="w-full px-3 py-1.5 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Search pages to link"
            />
          </div>

          {/* Results */}
          <div className="max-h-64 overflow-y-auto p-1">
            {loading && (
              <p className="px-3 py-4 text-sm text-muted-foreground text-center">Loading pages...</p>
            )}

            {!loading && filteredPages.length === 0 && (
              <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                {query ? 'No pages found.' : 'No pages available.'}
              </p>
            )}

            {!loading && filteredPages.length > 0 && (
              <ul role="listbox" className="space-y-0.5">
                {filteredPages.slice(0, 50).map((page) => (
                  <li key={page.pageId}>
                    <button
                      type="button"
                      role="option"
                      className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-accent transition-colors flex items-center gap-2"
                      onClick={() => handleSelect(page)}
                    >
                      <span
                        className="text-muted-foreground text-xs shrink-0"
                        style={{ marginLeft: `${page.depth * 12}px` }}
                      >
                        📄
                      </span>
                      <span className="truncate flex-1 min-w-0">{page.title}</span>
                      <span className="text-xs text-muted-foreground shrink-0 truncate max-w-[80px]">
                        {page.libraryName}
                      </span>
                    </button>
                  </li>
                ))}
                {filteredPages.length > 50 && (
                  <li className="px-3 py-1 text-xs text-muted-foreground text-center">
                    Showing first 50 results. Narrow your search.
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function flattenTree(
  nodes: PageTreeNode[],
  libraryId: string,
  libraryName: string,
  depth: number,
  out: FlatPage[]
): void {
  for (const node of nodes) {
    out.push({
      pageId: node.pageId,
      title: node.title,
      libraryId,
      libraryName,
      depth,
    });
    if (node.children && node.children.length > 0) {
      flattenTree(node.children, libraryId, libraryName, depth + 1, out);
    }
  }
}
